import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessApiService } from "../services/businessApiService";
import {
  ReminderSettingsSchema,
  toUpdateReminderSettingsDto,
  type Business,
  type ReminderSettings,
  type UpdateReminderSettingsRequest,
} from "../types/businessTypes";
import { businessKeys } from "./businessQueries";

export const useReminderSettings = (businessId: string) => {
  return useQuery<ReminderSettings, Error>({
    queryKey: businessKeys.reminderSettings(businessId),
    queryFn: async ({ signal }) => {
      const dto = await businessApiService.getReminderSettings(businessId, signal);
      return ReminderSettingsSchema.parse(dto);
    },
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateReminderSettings = (businessId: string) => {
  const queryClient = useQueryClient();

  return useMutation<ReminderSettings, Error, UpdateReminderSettingsRequest>({
    onMutate: () => queryClient.cancelQueries({ queryKey: businessKeys.reminderSettings(businessId) }),
    mutationFn: async (data) => {
      const dto = toUpdateReminderSettingsDto(data);
      const resultDto = await businessApiService.updateReminderSettings(businessId, dto);
      return ReminderSettingsSchema.parse(resultDto);
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(businessKeys.reminderSettings(businessId), settings);
      queryClient.setQueryData<Business>(businessKeys.detail(businessId), (previous) =>
        previous ? { ...previous, reminderSettings: settings } : undefined);
      queryClient.setQueryData<Business[]>(businessKeys.list(), (previous) =>
        previous?.map((business) => business.id === businessId
          ? { ...business, reminderSettings: settings }
          : business));
    },
  });
};
